import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import Breadcrumbs from "../../component/breadcrumbs/breadcrumbs";
import "./how-we-work.css";
import { useLanguage } from "../../i18n/LanguageContext";

const HowWeWork = () => {
  const { tr } = useLanguage();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const steps = [
    {
      number: "01",
      title: tr("Explore Your Needs", "Eksplorasi Kebutuhanmu"),
      description: tr(
        "Not sure what to build? No problem. Our AI consultants will walk you through your workflow, listen to your pain points, and help uncover where automation or AI could help",
        "Belum yakin mau membangun apa? Tenang. Konsultan AI kami akan menelusuri workflow‑mu, mendengar pain point, dan membantu menemukan area yang bisa dibantu automation atau AI."
      ),
      deliverable: tr("Clear understanding of your process", "Pemahaman jelas tentang prosesmu"),
    },
    {
      number: "02",
      title: tr("Co-Design the Solution", "Co‑Design Solusinya"),
      description: tr(
        "We'll brainstorm together and suggest AI-powered ideas tailored to how you work. No technical prep needed from you. This helps you decide whether to move forward or not",
        "Kita brainstorming bersama dan memberi ide AI sesuai cara kerjamu. Tidak perlu persiapan teknis. Ini membantu kamu memutuskan lanjut atau tidak."
      ),
      deliverable: tr("Potential AI solutions", "Rancangan solusi AI potensial"),
    },
    {
      number: "03",
      title: tr("Build Core Capability", "Build Kapabilitas Inti"),
      description: tr(
        "Once you're ready, we develop a working AI prototype or full build depending on your needs. You'll get something functional in <30 days",
        "Saat kamu siap, kami membangun prototype AI atau full build sesuai kebutuhanmu. Kamu akan mendapat hasil fungsional dalam <30 hari."
      ),
      deliverable: tr("Working AI with tool integration", "AI berfungsi dengan integrasi tool"),
    },
  ];

  return (
    <div className="how-we-work-page">
      <section className="how-we-work-section">
        <Breadcrumbs />
        <motion.h1
          className="how-we-work-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isMobile ? 0.3 : 0.5 }}
        >
          {tr("How We Work", "Cara Kerja")}
        </motion.h1>

        <div className="how-we-work-cards">
          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              className="how-we-work-card-wrapper"
              initial={{ opacity: 0, y: 30 }}
              {...(isMobile
                ? {
                    animate: { opacity: 1, y: 0 },
                    transition: { delay: i * 0.08, duration: 0.35 },
                  }
                : {
                    whileInView: { opacity: 1, y: 0 },
                    viewport: { once: true, margin: "-40px" },
                    transition: { delay: i * 0.12, duration: 0.5 },
                  }
                )}
            >
              <article className="how-we-work-card">
                <span className="how-we-work-number">{step.number}</span>
                <h2 className="how-we-work-card-title">{step.title}</h2>
                <p className="how-we-work-card-description">{step.description}</p>
                <div className="how-we-work-deliverable">
                  <span className="how-we-work-deliverable-label">
                    {tr("Deliverable:", "Deliverable:")}
                  </span>
                  <span className="how-we-work-deliverable-text">
                    {step.deliverable}
                  </span>
                </div>
              </article>
              {i < steps.length - 1 && (
                <div className="how-we-work-arrow" aria-hidden="true">
                  <ChevronRight size={24} />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HowWeWork;
